import React, { useEffect, useRef, useState } from "react";
import { authFetch, API_BASE } from "../api";
import MessageBubble from "./MessageBubble";
import TemplateGallery from "./TemplateGallery";
import "./ChatPane.css";

export default function ChatPane({
  messages,
  input,
  onInputChange,
  onSend,
  loading,
  pendingCode,
  isSaved,
  deploying,
  // Derived gate state from App.js — `{ allowed, reason }` tuples. Both
  // are optional so existing call sites (tests, older parents) don't break.
  saveGate,
  publishGate,
  onDeploy,
  onPublish,
  cmsConnected,
  integrationName,
  onDiscard,
  onReset,
  onAutoFix,
  onRebuild,
  onSendDirect,
  onConfigureKeys,
  onConfigureVars,
  onOpenHistory,
  onOpenRecipe,
  onRecipeSuggestionUse,
  onRecipeSuggestionContinue,
  appVersion,
  onFixPlanApprove,
  onFixPlanSkip,
  onBuildPlanApprove,
  onBuildPlanChange,
  onDesignQuestionsSubmit,
  onResumeConversation,
  onDismissResume,
  onBroadScopeApprove,
  onBroadScopeCancel,
  notificationPermission,
  onRequestNotificationPermission,
}) {
  const bottomRef = useRef(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [attachment, setAttachment] = useState(null); // { id, type, filename }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (attachment) {
      onSend(attachment);
      setAttachment(null);
    } else {
      onSend();
    }
  };

  const isStreaming = messages.length > 0 && messages[messages.length - 1]._streaming;

  return (
    <div className="chat-pane">
      {/* Context bar when editing an integration */}
      {integrationName && messages.length > 0 && (
        <div className="context-bar">
          <span className="context-dot" />
          <span className="context-name">{integrationName}</span>
          {onOpenHistory && (
            <button
              className="context-history"
              onClick={onOpenHistory}
              title="View version history — revert to any previous state"
            >
              ⟲ History
            </button>
          )}
          <button className="context-close" onClick={onReset} title="Start new integration">
            &times; New
          </button>
        </div>
      )}

      {messages.length === 0 && !showCustomInput && (
        <TemplateGallery
          onSelectTemplate={(t) => {
            onInputChange(t.prompt);
            setShowCustomInput(true);
          }}
          onCustom={() => setShowCustomInput(true)}
        />
      )}

      <div className="chat-messages" style={messages.length === 0 && !showCustomInput ? { display: "none" } : {}}>
        {messages.length === 0 && showCustomInput && (
          <div className="chat-empty-compact">
            <p>Describe what you want to display on your screens.</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} onAutoFix={onAutoFix} onRebuild={onRebuild} onConfigureKeys={onConfigureKeys} onOpenRecipe={onOpenRecipe} onRecipeSuggestionUse={onRecipeSuggestionUse} onRecipeSuggestionContinue={onRecipeSuggestionContinue} onFixPlanApprove={onFixPlanApprove} onFixPlanSkip={onFixPlanSkip} onBuildPlanApprove={onBuildPlanApprove} onBuildPlanChange={onBuildPlanChange} onDesignQuestionsSubmit={onDesignQuestionsSubmit} onResumeConversation={onResumeConversation} onDismissResume={onDismissResume} onBroadScopeApprove={onBroadScopeApprove} onBroadScopeCancel={onBroadScopeCancel} isSaved={isSaved} />
        ))}

        {loading && !isStreaming && (
          <div className="chat-loading">
            <div className="typing-indicator">
              <span /><span /><span />
            </div>
            <span>Thinking...</span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {pendingCode && !isSaved && (
        <div className="deploy-bar">
          <span>
            Looks good? Save it to make it available in My Apps.
            {appVersion != null && <span className="version-badge">v{appVersion}</span>}
          </span>
          <div className="deploy-bar-actions">
            {onConfigureKeys && (
              <button className="keys-bar-btn" onClick={onConfigureKeys}>
                🔑 API Keys
              </button>
            )}
            {onConfigureVars && (
              <button
                className="keys-bar-btn"
                onClick={onConfigureVars}
                title="Non-secret config values — base URLs, tenant IDs, feature flags"
              >
                ⚙ Variables
              </button>
            )}
            <button
              className="discard-btn"
              onClick={onDiscard}
              disabled={deploying || loading}
              title={loading ? "Wait for the current response to finish" : undefined}
            >
              Discard
            </button>
            <button
              className="deploy-btn"
              onClick={onDeploy}
              // Prefer the structured gate if present; fall back to the
              // original flags so older callers still get the same behavior.
              disabled={saveGate ? !saveGate.allowed : (deploying || loading)}
              title={
                saveGate && !saveGate.allowed ? saveGate.reason :
                loading ? "Wait for the current response to finish" : undefined
              }
            >
              {deploying ? "Saving..." : loading ? "Thinking…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {pendingCode && isSaved && (
        <div className="deploy-bar saved-bar">
          <span>
            Your app is saved! Open My Apps to publish it to devices, or keep refining here.
            {appVersion != null && <span className="version-badge">v{appVersion}</span>}
          </span>
          <div className="deploy-bar-actions">
            {onConfigureKeys && (
              <button className="keys-bar-btn" onClick={onConfigureKeys}>
                🔑 API Keys
              </button>
            )}
            {onConfigureVars && (
              <button
                className="keys-bar-btn"
                onClick={onConfigureVars}
                title="Non-secret config values — base URLs, tenant IDs, feature flags"
              >
                ⚙ Variables
              </button>
            )}
            {onPublish && (
              <button
                className="deploy-btn"
                onClick={onPublish}
                disabled={publishGate && !publishGate.allowed}
                title={publishGate && !publishGate.allowed ? publishGate.reason : undefined}
              >
                Publish
              </button>
            )}
          </div>
        </div>
      )}

      {!pendingCode && onPublish && (
        <div className="deploy-bar publish-bar">
          <span>
            Integration running
            {appVersion != null && <span className="version-badge">v{appVersion}</span>}
          </span>
          <button
            className="publish-btn"
            onClick={onPublish}
            disabled={publishGate && !publishGate.allowed}
            title={publishGate && !publishGate.allowed ? publishGate.reason : undefined}
          >
            Publish to Devices
          </button>
        </div>
      )}

      {/* Attachment chip */}
      {attachment && (
        <div className="attachment-bar">
          <div className="attachment-chip">
            <span className="attachment-icon">{attachment.type === "image" ? "🖼" : "📄"}</span>
            <span className="attachment-name">{attachment.filename}</span>
            <button className="attachment-remove" onClick={() => setAttachment(null)}>&times;</button>
          </div>
          <span className="attachment-hint">Add a description and press Send</span>
        </div>
      )}

      {notificationPermission === "denied" && (
        <div className="notification-denied-bar">
          <span>🔔 Notifications blocked — enable them in your browser settings to get build alerts.</span>
        </div>
      )}

      <div className="chat-input-area">
        <label className="upload-btn" title="Upload CSV or screenshot">
          <input
            type="file"
            accept=".csv,.tsv,.xlsx,.png,.jpg,.jpeg,.gif,.webp"
            style={{ display: "none" }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const form = new FormData();
              form.append("file", file);
              const isImage = file.type.startsWith("image/");
              try {
                if (isImage) {
                  const res = await authFetch(`${API_BASE}/upload/image`, { method: "POST", body: form });
                  const data = await res.json();
                  if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Upload failed");
                  setAttachment({ id: data.id, type: "image", filename: data.filename });
                } else {
                  const res = await authFetch(`${API_BASE}/upload/csv`, { method: "POST", body: form });
                  const data = await res.json();
                  if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : data.error?.message || "Upload failed");
                  setAttachment({ id: data.id, type: "csv", filename: data.filename, headers: data.headers, rowCount: data.rowCount });
                }
                setShowCustomInput(true);
              } catch (err) {
                console.error("Upload failed:", err);
              }
              e.target.value = "";
            }}
          />
          &#128206;
        </label>
        <textarea
          className="chat-input"
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={attachment ? "Describe what you want (optional)..." : "Describe what you want to display..."}
          rows={4}
          disabled={loading}
        />
        <button
          className="send-btn"
          onClick={handleSend}
          disabled={loading || (!input.trim() && !attachment)}
        >
          &#10148;
        </button>
      </div>
    </div>
  );
}
